import {api} from './api';

export interface AiBacktestTask {
    id: number;
    taskName: string;
    userId: number;
    instId: string;
    exchange: string;
    klineTimeframe: string;
    decisionFrequency: string;
    startTime: number;
    endTime: number;
    initialCapital: number;
    modelName: string;
    maxPositions: number;
    maxAmountPerTrade: number;
    leverage: number;
    status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'STOPPED';
    progress: number;
    currentDecisionTime: number;
    progressMessage?: string;
    progressKlineCount?: number;
    createdAt: string;
    updatedAt: string;
}

export interface AiBacktestResult {
    id: number;
    taskId: number;
    totalReturnPct: number;
    maxDrawdownPct: number;
    sharpeRatio: number;
    winRate: number;
    profitLossRatio: number;
    maxConsecutiveLoss: number;
    totalTrades: number;
    totalDecisions: number;
    positionRatio: number;
    btcBenchmarkPct: number;
    ethBenchmarkPct: number;
    equityCurveJson: string;
    benchmarkCurveJson: string;
    tradesJson: string;
    decisionsJson: string;
    positionRecordsJson: string;
    finalBalance: number;
}

export interface AiBacktestDecision {
    id: number;
    taskId: number;
    decisionTime: number;
    klineTime: number;
    action: string;
    confidence: number;
    instId: string;
    price: number;
    reasoning: string;
    promptContent: string;
    responseContent: string;
    llmCallTimeMs: number;
    tradeOrderId: number;
}

export interface KlineCoverage {
    instId: string;
    timeframe: string;
    dataSource: string;
    minTime: number;
    maxTime: number;
    totalBars: number;
}

export interface KlineDataItem {
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

export interface FreqtradeDataFile {
    exchange: string;
    pair: string;
    timeframe: string;
    path: string;
    fileSize: number;
}

export interface ImportResult {
    fileName: string;
    successCount: number;
    skipCount: number;
    elapsedMs: number;
    error: string;
}

export const aiBacktestService = {
    startBacktest(params: {
        taskName: string;
        userId: number;
        instId: string;
        exchange?: string;
        decisionFrequency: string;
        startTime: number;
        endTime: number;
        initialCapital: number;
        modelName: string;
        maxPositions?: number;
        maxAmountPerTrade?: number;
        leverage?: number;
    }) {
        return api.post('/api/v1/ai-backtest/start', params);
    },

    stopBacktest(taskId: number) {
        return api.post(`/api/v1/ai-backtest/${taskId}/stop`);
    },

    rerunBacktest(taskId: number) {
        return api.post(`/api/v1/ai-backtest/${taskId}/rerun`);
    },

    getStatus(taskId: number) {
        return api.get(`/api/v1/ai-backtest/${taskId}/status`);
    },

    getResult(taskId: number) {
        return api.get(`/api/v1/ai-backtest/${taskId}/result`);
    },

    getKlines(taskId: number, timeframe?: string) {
        let url = `/api/v1/ai-backtest/${taskId}/klines`;
        if (timeframe) url += `?timeframe=${timeframe}`;
        return api.get(url);
    },

    getKlinesWithIndicators(taskId: number, timeframe: string, indicators: string[]) {
        let url = `/api/v1/ai-backtest/${taskId}/klines-with-indicators?timeframe=${timeframe}`;
        if (indicators.length > 0) url += `&indicators=${indicators.join(',')}`;
        return api.get(url);
    },

    getDecisions(taskId: number, page = 0, size = 20, action?: string) {
        let url = `/api/v1/ai-backtest/${taskId}/decisions?page=${page}&size=${size}`;
        if (action) url += `&action=${action}`;
        return api.get(url);
    },

    getDecisionDetail(taskId: number, decisionId: number) {
        return api.get(`/api/v1/ai-backtest/${taskId}/decision/${decisionId}`);
    },

    getTasks(userId: number, status?: string) {
        let url = `/api/v1/ai-backtest/tasks?userId=${userId}`;
        if (status) url += `&status=${status}`;
        return api.get(url);
    },

    deleteTask(taskId: number) {
        return api.delete(`/api/v1/ai-backtest/${taskId}`);
    },

    getKlineCoverage(instId?: string, timeframe?: string) {
        let url = '/api/v1/ai-backtest/kline-coverage';
        const params: string[] = [];
        if (instId) params.push(`instId=${instId}`);
        if (timeframe) params.push(`timeframe=${timeframe}`);
        if (params.length > 0) url += '?' + params.join('&');
        return api.get(url);
    },

    listFreqtradeFiles(userDataDir = '/tmp/main-docker') {
        return api.get(`/api/v1/ai-backtest/freqtrade-files?userDataDir=${encodeURIComponent(userDataDir)}`);
    },

    importFreqtradeData(userDataDir = '/tmp/main-docker', dataSource = 'paper') {
        return api.post('/api/v1/ai-backtest/import-freqtrade', {userDataDir, dataSource});
    },

    getAvailableModels() {
        return api.get('/api/v1/ai-backtest/available-models');
    },

    estimate(params: {
        instId: string;
        decisionFrequency: string;
        startTime: number;
        endTime: number;
    }) {
        return api.post('/api/v1/ai-backtest/estimate', params);
    }
};
